import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, ChevronDown, Loader2, Wand2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { RewriteAll } from "./RewriteAll";

const tone = (s, bar) => (s >= 85 ? "bg-emerald-50 text-emerald-800 border-emerald-200" : s >= bar ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-red-50 text-red-700 border-red-200");

const StyleRow = ({ p, bar, kitId, reload, onOpen, fixing, onFix, locked }) => {
  const toggle = async (e) => {
    e.stopPropagation();
    try { await http.post(`/kits/${kitId}/style-accept`, { block: p.block, accept: !p.accepted }); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <li data-testid={`style-row-${p.n}`} onClick={() => onOpen?.(p.n - 1)} className="flex items-start gap-3 border-t border-stone-100 py-2 cursor-pointer hover:bg-[#C9A847]/5 transition-colors">
      <span data-testid={`style-score-${p.n}`} className={`shrink-0 w-11 text-center font-mono-ui text-xs border rounded-sm py-0.5 ${tone(p.score, bar)}`}>{p.score}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2B2A26]">p.{p.n} · {p.label}{p.accepted && <span className="ml-2 text-[11px] text-sky-700">accepted</span>}</div>
        {p.reasons.length > 0 && <div className="text-xs text-stone-500">{p.reasons.join(" · ")}</div>}
      </div>
      {p.score < bar && !p.accepted && (
        <button data-testid={`style-fix-${p.n}`} disabled={fixing || locked} onClick={(e) => { e.stopPropagation(); onFix(p.block); }}
          className="shrink-0 inline-flex items-center gap-1 rounded-sm bg-[#2B2A26] px-2 py-1 text-[11px] font-bold text-white hover:bg-black transition-colors disabled:opacity-60">
          {fixing ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} {fixing ? "Rewriting…" : "Rewrite in house style"}
        </button>
      )}
      {p.score < bar && <button data-testid={`style-accept-${p.n}`} onClick={toggle} className="shrink-0 text-[11px] underline text-stone-600 hover:text-black">{p.accepted ? "Undo" : "Accept as is"}</button>}
    </li>
  );
};

export const StylePanel = ({ kit, onOpen, reloadKit }) => {
  const [r, setR] = useState(null);
  const [all, setAll] = useState(false);
  const fixing = kit.style_fixing || [];
  const runningAll = kit.style_fix_all?.status === "running";
  const load = useCallback(() => http.get(`/kits/${kit.id}/style`).then((x) => setR(x.data)).catch(() => {}), [kit.id]);
  useEffect(() => { load(); }, [load, kit.page_check?.at, kit.updated_at, fixing.length, kit.style_fix_all?.done?.length]);
  useEffect(() => {
    if ((!fixing.length && !runningAll) || !reloadKit) return undefined;
    const t = setInterval(reloadKit, 6000);
    return () => clearInterval(t);
  }, [fixing.length, runningAll, reloadKit]);
  const fix = async (block) => {
    try { await http.post(`/kits/${kit.id}/style-fix`, { block }); toast.message("Rewriting this page in your house style — about a minute"); reloadKit?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!r?.pages?.length) return null;
  const list = [...r.pages].sort((a, b) => a.score - b.score).filter((p) => all || p.score < r.bar || p.accepted);
  return (
    <section data-testid="style-panel" className="mb-6 rounded-lg border border-stone-200 bg-[#FAF7F0] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Gauge size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Style Score</b>
        <span data-testid="style-average" className={`font-mono-ui text-sm border rounded-sm px-2 py-0.5 ${tone(r.average, r.bar)}`}>{r.average} / 100</span>
        <span data-testid="style-weak" className={`text-sm ${r.weak ? "text-red-700" : "text-emerald-700"}`}>
          {r.weak ? `${r.weak} page(s) below ${r.bar} — publishing is blocked until they're fixed or accepted` : `Every page scores ${r.bar}+ against your uploaded guides`}
        </span>
        <button data-testid="style-toggle-all" onClick={() => setAll(!all)} className="ml-auto inline-flex items-center gap-1 text-xs text-stone-600 hover:text-black">
          {all ? "Weak pages only" : `All ${r.pages.length} pages`} <ChevronDown size={13} className={`transition-transform ${all ? "rotate-180" : ""}`} />
        </button>
      </div>
      {r.guide && <p data-testid="style-guide-name" className="text-xs text-stone-600 mt-1">Scored against your chosen guide: <b>{r.guide.name}</b>{r.guide.avg ? ` (paragraphs ~${r.guide.avg} words)` : ""}</p>}
      {(r.weak > 0 || kit.style_fix_all) && <RewriteAll kit={kit} weakBlocks={r.pages.filter((p) => !p.ok).map((p) => p.block)} reloadKit={reloadKit} />}
      {kit.style_fix_error && <p data-testid="style-fix-error" className="text-xs text-red-700 mt-1">Last rewrite didn't save — {kit.style_fix_error}</p>}
      <p className="text-xs text-stone-500 mt-1">The page check re-runs on its own after each rewrite, so scores refresh within about a minute. Measures each page against your guides: how full it is, paragraph length, filler words, report-style phrasing, addresses in the prose, names in capitals and repeated sentences.</p>
      {list.length > 0 && <ul className="mt-2 max-h-72 overflow-y-auto">{list.map((p) => <StyleRow key={`${p.block}-${p.n}`} p={p} bar={r.bar} kitId={kit.id} reload={load} onOpen={onOpen} fixing={fixing.includes(p.block)} locked={runningAll} onFix={fix} />)}</ul>}
    </section>
  );
};
